import { useEffect, useMemo, useRef, useState } from "react";
import { BookHeart } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import OathboundFrame from "@/components/OathboundFrame/OathboundFrame";
import SpecularButton from "@/components/SpecularButton/SpecularButton";
import { postPurchaseApi } from "@/components/PurchasePage/purchaseCheckout";
import {
  buildReviewPayload,
  readReviewToken,
  REVIEW_TEXT_MAX_LENGTH,
  reviewCharacters,
  validateReviewForm,
} from "./reviewForm";
import "./ReviewPage.css";

const initialValues = {
  firstName: "",
  lastName: "",
  reviewText: "",
  favoriteCharacter: "",
};

function FieldError({ id, message }) {
  return (
    <span className="review-field-error" id={id} aria-hidden={!message}>
      {message || "\u00a0"}
    </span>
  );
}

export default function ReviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const token = useMemo(() => readReviewToken(location.hash), [location.hash]);
  const [accessPhase, setAccessPhase] = useState("checking");
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [formMessage, setFormMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const validateInvitation = async () => {
      if (!token) {
        navigate("/#purchase", { replace: true });
        return;
      }

      try {
        const response = await postPurchaseApi("/api/review-invitation", {
          token,
        });
        if (cancelled) return;
        if (!response?.success || !response.valid) {
          navigate("/#purchase", { replace: true });
          return;
        }
        setAccessPhase("ready");
      } catch {
        if (!cancelled) navigate("/#purchase", { replace: true });
      }
    };

    validateInvitation();
    return () => {
      cancelled = true;
    };
  }, [navigate, token]);

  const updateValue = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => {
        const nextValues = { ...values, [name]: value };
        const next = { ...current };
        const message = validateReviewForm(nextValues)[name];
        if (message) next[name] = message;
        else delete next[name];
        return next;
      });
    }
    if (formMessage) setFormMessage("");
  };

  const submitReview = async (event) => {
    event.preventDefault();
    if (accessPhase !== "ready") return;

    const nextErrors = validateReviewForm(values);
    setErrors(nextErrors);
    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      const field = formRef.current?.elements.namedItem(firstInvalidField);
      if (field instanceof RadioNodeList) field[0]?.focus();
      else field?.focus();
      return;
    }

    setAccessPhase("submitting");
    setFormMessage("");
    try {
      const response = await postPurchaseApi(
        "/api/submit-review",
        buildReviewPayload(token, values),
      );

      // Both an accepted review and a silently rejected invitation leave no
      // token-bearing page behind in browser history.
      if (response?.success) {
        navigate("/#purchase", { replace: true });
        return;
      }
      navigate("/#purchase", { replace: true });
    } catch (error) {
      if (error?.code === "INVALID_REVIEW") {
        setAccessPhase("ready");
        setFormMessage(error.message);
        return;
      }
      if (error?.code === "INVALID_REVIEW_TOKEN") {
        navigate("/#purchase", { replace: true });
        return;
      }
      setAccessPhase("ready");
      setFormMessage("Your review could not be submitted. Please try again.");
    }
  };

  return (
    <main className="review-page" aria-labelledby="review-page-title">
      <OathboundFrame />

      <section className="review-content">
        <article className="review-card">
          <header className="review-heading">
            <img src="/media/images/Oath_logo.avif" alt="The Oath of the Unhuan" />
            <p>Reader invitation</p>
            <h1 id="review-page-title">Share your journey</h1>
            <span>
              Your thoughts help other readers find their way into the world of
              the Pehuans.
            </span>
          </header>

          {accessPhase === "checking" ? (
            <div className="review-loading" role="status">
              <BookHeart aria-hidden="true" />
              <p>Opening your invitation…</p>
            </div>
          ) : (
            <form ref={formRef} onSubmit={submitReview} noValidate>
              <fieldset className="review-section">
                <legend>Your name</legend>
                <div className="review-name-grid">
                  <label className={errors.firstName ? "has-error" : ""}>
                    <span>First name</span>
                    <input
                      name="firstName"
                      type="text"
                      value={values.firstName}
                      onChange={updateValue}
                      autoComplete="given-name"
                      maxLength="60"
                      aria-invalid={Boolean(errors.firstName)}
                      aria-describedby="review-first-name-error"
                    />
                    <FieldError
                      id="review-first-name-error"
                      message={errors.firstName}
                    />
                  </label>
                  <label className={errors.lastName ? "has-error" : ""}>
                    <span>Last name</span>
                    <input
                      name="lastName"
                      type="text"
                      value={values.lastName}
                      onChange={updateValue}
                      autoComplete="family-name"
                      maxLength="60"
                      aria-invalid={Boolean(errors.lastName)}
                      aria-describedby="review-last-name-error"
                    />
                    <FieldError
                      id="review-last-name-error"
                      message={errors.lastName}
                    />
                  </label>
                </div>
              </fieldset>

              <fieldset className="review-section">
                <legend>Your review</legend>
                <label className={`review-body-field${errors.reviewText ? " has-error" : ""}`}>
                  <span>What stayed with you?</span>
                  <textarea
                    name="reviewText"
                    value={values.reviewText}
                    onChange={updateValue}
                    maxLength={REVIEW_TEXT_MAX_LENGTH}
                    rows="7"
                    aria-invalid={Boolean(errors.reviewText)}
                    aria-describedby="review-body-error review-character-count"
                    placeholder="Share what you felt, noticed, or carried with you after reading…"
                  />
                  <span className="review-body-meta">
                    <FieldError id="review-body-error" message={errors.reviewText} />
                    <output id="review-character-count">
                      {values.reviewText.length}/{REVIEW_TEXT_MAX_LENGTH}
                    </output>
                  </span>
                </label>
              </fieldset>

              <fieldset
                className={`review-section review-character-section${
                  errors.favoriteCharacter ? " has-error" : ""
                }`}
              >
                <legend>Who was your favourite character?</legend>
                <div className="review-character-grid">
                  {reviewCharacters.map((character) => (
                    <label key={character}>
                      <input
                        type="radio"
                        name="favoriteCharacter"
                        value={character}
                        checked={values.favoriteCharacter === character}
                        onChange={updateValue}
                      />
                      <span>{character}</span>
                    </label>
                  ))}
                </div>
                <FieldError
                  id="review-favorite-character-error"
                  message={errors.favoriteCharacter}
                />
              </fieldset>

              <p className="review-privacy-note">
                Your name, review, and favourite character may be displayed
                publicly after moderation.
              </p>

              <SpecularButton
                className="review-submit"
                type="submit"
                disabled={accessPhase === "submitting"}
                ariaBusy={accessPhase === "submitting"}
              >
                {accessPhase === "submitting" ? "Submitting…" : "Submit review"}
              </SpecularButton>

              {formMessage && (
                <p className="review-form-message" role="alert">
                  {formMessage}
                </p>
              )}
            </form>
          )}
        </article>

        <Link className="review-return-home" to="/#purchase">
          Return home
        </Link>
      </section>
    </main>
  );
}
